import React, { useState } from "react";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { Badge, Box, Button, Checkbox, FormControlLabel, Popover, Stack, TextField } from "@mui/material";
import { salesRepDisplayName } from "@cpt/shared";
import { OrderStatus, SalesMotion } from "client/api-v2";
import { orderStatusLabel } from "areas/orders/utils/orderStatusLabel";

export const allFilterValue = "All";

export const reviewFilterValues = {
  all: allFilterValue,
  needsReview: "InReview",
  noReviewRequired: "NotInReview",
} as const;

export const salesRepFilterValues = {
  all: allFilterValue,
  selfServe: "Self-serve",
} as const;

export const archivedFilterValues = {
  excluded: allFilterValue,
  included: "Included",
} as const;

export type QuotesListFilters = {
  status: string;
  fulfilment: string;
  review: string;
  motion: string;
  salesRep: string;
  existingSubscription: string;
  quoteSubscription: string;
  archived: string;
};

export const quotesListFilterKeys: (keyof QuotesListFilters)[] = [
  "status",
  "fulfilment",
  "review",
  "motion",
  "salesRep",
  "existingSubscription",
  "quoteSubscription",
  "archived",
];

export const noQuotesListFilters: QuotesListFilters = {
  status: allFilterValue,
  fulfilment: allFilterValue,
  review: allFilterValue,
  motion: allFilterValue,
  salesRep: allFilterValue,
  existingSubscription: allFilterValue,
  quoteSubscription: allFilterValue,
  archived: archivedFilterValues.excluded,
};

type FilterOption = { value: string; label: string; disabled?: boolean };

type FilterDefinition = {
  key: keyof QuotesListFilters;
  label: string;
  options: FilterOption[];
  group?: { label: string; options: FilterOption[] };
  unavailableReason?: string;
  helperText?: string;
  acceptsAnyValue?: boolean;
};

const existingSubscriptionOptions = [
  "Free",
  "Trial",
  "Starter",
  "Professional",
  "Enterprise",
  "Platform",
  "Platform - Forecast",
  "Platform - Startup",
];

const quoteSubscriptionOptions = ["Cloud", "Self-hosted", "Platform", "Platform - Forecast", "Platform - Startup"];

const fulfilmentOptions = [
  "Not yet issued",
  "Awaiting acceptance",
  "Accepted, awaiting internal confirmation",
  "Accepted, awaiting fulfillment approval",
  "Accepted",
  "Canceled",
  "Expired",
];

const readThePlanColumnInstead = "This filter is not available yet. Read the Plan column on each row.";

function asOptions(values: string[]): FilterOption[] {
  return values.map((value) => ({ value, label: value }));
}

function filterDefinitions(salesReps: string[]): FilterDefinition[] {
  return [
    {
      key: "status",
      label: "Status",
      options: [
        { value: allFilterValue, label: "All statuses" },
        ...Object.values(OrderStatus).map((status) => ({ value: status, label: orderStatusLabel(status) })),
      ],
    },
    {
      key: "fulfilment",
      label: "Fulfillment",
      options: [{ value: allFilterValue, label: "All fulfillments" }, ...asOptions(fulfilmentOptions)],
      unavailableReason: "This filter is not available yet. Read the Fulfillment column on each row.",
    },
    {
      key: "review",
      label: "Review",
      options: [
        { value: reviewFilterValues.all, label: "All orders" },
        { value: reviewFilterValues.needsReview, label: "Needs review" },
        { value: reviewFilterValues.noReviewRequired, label: "No review required" },
      ],
    },
    {
      key: "motion",
      label: "Motion",
      options: [{ value: allFilterValue, label: "All motions" }, ...asOptions(Object.values(SalesMotion))],
    },
    {
      key: "salesRep",
      label: "Sales Rep",
      options: [
        { value: salesRepFilterValues.all, label: "All" },
        { value: salesRepFilterValues.selfServe, label: "Self-serve" },
      ],
      group:
        salesReps.length > 0
          ? {
              label: "Reps",
              options: salesReps.map((salesRep) => ({ value: salesRep, label: salesRepDisplayName(salesRep) })),
            }
          : undefined,
      helperText: salesReps.length > 0 ? undefined : "No representative owns an order, so only Self-serve narrows it.",
      acceptsAnyValue: true,
    },
    {
      key: "existingSubscription",
      label: "Existing Subscription",
      options: [{ value: allFilterValue, label: "All subscriptions" }, ...asOptions(existingSubscriptionOptions)],
      unavailableReason: readThePlanColumnInstead,
    },
    {
      key: "quoteSubscription",
      label: "Order Subscription",
      options: [{ value: allFilterValue, label: "All subscriptions" }, ...asOptions(quoteSubscriptionOptions)],
      unavailableReason: readThePlanColumnInstead,
    },
  ];
}

function isFilterApplied(definition: FilterDefinition, value: string): boolean {
  if (value === allFilterValue || definition.unavailableReason !== undefined) {
    return false;
  }

  if (definition.acceptsAnyValue === true) {
    return true;
  }

  return [...definition.options, ...(definition.group?.options ?? [])].some(
    (option) => option.value === value && option.disabled !== true
  );
}

export function countActiveQuotesListFilters(filters: QuotesListFilters, salesReps: string[] = []): number {
  const activeSelects = filterDefinitions(salesReps).filter((definition) =>
    isFilterApplied(definition, filters[definition.key])
  ).length;

  return activeSelects + (filters.archived === archivedFilterValues.included ? 1 : 0);
}

function FilterOptions({ options }: { options: FilterOption[] }) {
  return (
    <>
      {options.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </>
  );
}

export function FilterPopover({
  filters,
  salesReps = [],
  onFilterChange,
  onResetFilters,
}: {
  filters: QuotesListFilters;
  salesReps?: string[];
  onFilterChange: (key: keyof QuotesListFilters, value: string) => void;
  onResetFilters: () => void;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <Badge badgeContent={countActiveQuotesListFilters(filters, salesReps)} color="primary">
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<FilterAltOutlinedIcon />}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          Filter
        </Button>
      </Badge>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Stack spacing={2} sx={{ p: 2.5, width: 300 }}>
          {filterDefinitions(salesReps).map((definition) => (
            <TextField
              key={definition.key}
              select
              size="small"
              fullWidth
              label={definition.label}
              value={filters[definition.key]}
              disabled={definition.unavailableReason !== undefined}
              helperText={definition.unavailableReason ?? definition.helperText}
              onChange={(event) => onFilterChange(definition.key, event.target.value)}
              slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
            >
              <FilterOptions options={definition.options} />
              {definition.group && (
                <optgroup label={definition.group.label}>
                  <FilterOptions options={definition.group.options} />
                </optgroup>
              )}
            </TextField>
          ))}
          <FormControlLabel
            label="Include archived"
            control={
              <Checkbox
                checked={filters.archived === archivedFilterValues.included}
                onChange={(event) =>
                  onFilterChange(
                    "archived",
                    event.target.checked ? archivedFilterValues.included : archivedFilterValues.excluded
                  )
                }
              />
            }
          />
          <Box>
            <Button variant="text" onClick={onResetFilters}>
              Reset filters
            </Button>
          </Box>
        </Stack>
      </Popover>
    </>
  );
}
